import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowUp, ArrowDown, Equals, ArrowsClockwise, Drop, Lightning, GasPump, Flame, Thermometer
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

const COMMODITY_CONFIG = {
  crude_oil: { icon: Drop, color: "#854d0e", label: "WTI Crude Oil" },
  brent: { icon: Drop, color: "#a16207", label: "Brent Crude" },
  natural_gas: { icon: Flame, color: "#0891b2", label: "Natural Gas" },
  gasoline: { icon: GasPump, color: "#dc2626", label: "RBOB Gasoline" },
  heating_oil: { icon: Thermometer, color: "#ea580c", label: "Heating Oil" },
};

function ChangeIndicator({ value, suffix = "" }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${isZero ? "text-zinc-400" : isPositive ? "text-emerald-400" : "text-red-400"}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value.toFixed(2)}{suffix}
    </span>
  );
}

function CommodityCard({ id, data }) {
  const cfg = COMMODITY_CONFIG[id];
  if (!cfg || !data || !data.current_price) return null;
  const Icon = cfg.icon;

  return (
    <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-5 space-y-4" data-testid={`oil-card-${id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${cfg.color}20` }}>
            <Icon size={16} weight="bold" style={{ color: cfg.color }} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-zinc-100">{data.name}</h3>
            <span className="text-[10px] text-zinc-500">{data.date}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold font-data text-zinc-50">${data.current_price}</p>
          <span className="text-[10px] text-zinc-500 uppercase">{data.unit}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3">
          <span className="text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">Day Change</span>
          <ChangeIndicator value={data.change_pct} suffix="%" />
        </div>
        <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3">
          <span className="text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">6M Range</span>
          <span className="text-xs font-data text-zinc-300">${data.low_6m} — ${data.high_6m}</span>
        </div>
      </div>
    </div>
  );
}

export default function OilGasReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState("6m");
  const [selectedCommodity, setSelectedCommodity] = useState("crude_oil");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/oil-gas`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to load oil/gas data:", e);
    } finally {
      setLoading(false);
    }
  };

  const chartData = data?.[selectedCommodity]?.[chartView === "5y" ? "history_5y" : "history_6m"] || [];
  const selectedName = data?.[selectedCommodity]?.name || "WTI Crude Oil";
  const estGas = data?.estimated_retail_gas;

  return (
    <div className="p-5 space-y-5 max-w-[1400px]" data-testid="oil-gas-page">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-zinc-50 tracking-tight flex items-center gap-2">
            <Drop size={20} weight="bold" className="text-amber-500" />
            Crude Oil & Gas Prices
          </h2>
          <p className="text-xs text-zinc-500 mt-1">Live futures prices via Yahoo Finance</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-amber-500/20 rounded-sm"
          data-testid="oil-refresh-btn"
        >
          <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Estimated Retail Gas Banner */}
      {estGas && (
        <div className="bg-amber-950/20 border border-amber-500/30 rounded-sm p-4 flex items-center gap-4" data-testid="retail-gas-banner">
          <GasPump size={28} weight="fill" className="text-amber-400 shrink-0" />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-data text-amber-300">${estGas.price}/gal</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-900/50 text-amber-400">EST. NATIONAL AVG</Badge>
            </div>
            <p className="text-[10px] text-zinc-500 mt-0.5">{estGas.note}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-40 bg-zinc-900/60" />)}
        </div>
      ) : (
        <>
          {/* Commodity Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(COMMODITY_CONFIG).map(([key]) => (
              <CommodityCard key={key} id={key} data={data?.[key]} />
            ))}
          </div>

          {/* Chart Section */}
          <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm p-5 space-y-3" data-testid="oil-chart-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(COMMODITY_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCommodity(key)}
                    data-testid={`chart-select-${key}`}
                    className={`text-[10px] font-medium px-2.5 py-1 rounded-sm border transition-colors ${
                      selectedCommodity === key
                        ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                        : "bg-transparent border-zinc-800/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"
                    }`}
                  >
                    {cfg.label}
                  </button>
                ))}
              </div>
              <Tabs value={chartView} onValueChange={setChartView}>
                <TabsList className="h-7 bg-zinc-900/60 border border-amber-500/15 p-0.5 gap-0.5">
                  <TabsTrigger value="6m" className="text-[10px] px-2 py-1 h-6 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300" data-testid="chart-6m">6M</TabsTrigger>
                  <TabsTrigger value="5y" className="text-[10px] px-2 py-1 h-6 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300" data-testid="chart-5y">5Y</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="h-64" data-testid="oil-chart">
              {chartData.length > 0 ? (
                <CpiChart data={chartData} color={COMMODITY_CONFIG[selectedCommodity]?.color || "#f59e0b"} label={selectedName} />
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No chart data available</div>
              )}
            </div>
          </div>
        </>
      )}

      {data?.fetched_at && (
        <div className="flex items-center justify-between pt-2 border-t border-amber-500/15" data-testid="oil-footer">
          <span className="text-[10px] text-zinc-600">Source: {data.source}</span>
          <span className="text-[10px] text-zinc-600">Updated: {new Date(data.fetched_at).toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}
